import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase, DatabaseMosque } from '../lib/supabase';
import { useAdmin } from '../hooks/useAdmin';
import { usePhotos } from '../hooks/usePhotos';
import { useDebounce } from '../hooks/useDebounce';
import { useLanguage } from '../contexts/LanguageContext';
import { X, Check, HelpCircle, RefreshCw, Images, Search } from 'lucide-react';
import '../styles/AdminDashboard.css';

interface CacheEntry<T> {
  data: T[];
  count: number;
  timestamp: number;
}

interface AdminDashboardProps {
  onClose: () => void;
}

type TabType = 'pending' | 'approved' | 'rejected';
type ViewType = 'mosques' | 'editSuggestions' | 'photos';

interface EditSuggestion {
  id: string;
  mosque_id: string;
  suggested_name: string | null;
  suggested_type: string | null;
  suggested_is_public: boolean | null;
  suggested_latitude: number | null;
  suggested_longitude: number | null;
  suggested_description: string | null;
  suggested_men_wc: boolean | null;
  suggested_women_wc: boolean | null;
  suggested_men_wudu: boolean | null;
  suggested_women_wudu: boolean | null;
  suggested_women_prayer_area: boolean | null;
  submitted_by_name: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  mosques: {
    name: string;
    type: string;
    is_public: boolean;
    description: string | null;
    men_wc: boolean | null;
    women_wc: boolean | null;
    men_wudu: boolean | null;
    women_wudu: boolean | null;
    women_prayer_area: boolean | null;
    latitude: number;
    longitude: number;
  } | null;
}

interface PendingPhoto {
  id: string;
  mosque_id: string;
  storage_path: string;
  file_name: string;
  caption: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  rejection_reason: string | null;
  photo_url: string;
  mosques: {
    name: string;
  } | null;
}

const REPORT_PREFIX = '[REPORT:';
const PHOTO_BUCKET = 'mosque-photos';

const escapePostgrestLikeValue = (value: string): string => {
  return value.replace(/[\\,%]/g, '\\$&');
};

// Helper to get user-friendly error message
const getErrorMessage = (err: any, t: (key: string) => string): string => {
  const message = err?.message || '';
  if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('INTERNET_DISCONNECTED')) {
    return t('admin.networkError');
  }
  return message;
};

const parseReportReason = (reason: string | null) => {
  if (!reason || !reason.startsWith(REPORT_PREFIX)) {
    return { isReport: false, code: null as string | null, details: reason };
  }

  const markerEnd = reason.indexOf(']');
  if (markerEnd === -1) {
    return { isReport: true, code: null as string | null, details: reason };
  }

  const code = reason.slice(REPORT_PREFIX.length, markerEnd);
  const details = reason.slice(markerEnd + 1).trim();
  return { isReport: true, code, details };
};

const hasSuggestedFieldChanges = (suggestion: EditSuggestion) => {
  return [
    suggestion.suggested_name,
    suggestion.suggested_type,
    suggestion.suggested_is_public,
    suggestion.suggested_latitude,
    suggestion.suggested_longitude,
    suggestion.suggested_description,
    suggestion.suggested_men_wc,
    suggestion.suggested_women_wc,
    suggestion.suggested_men_wudu,
    suggestion.suggested_women_wudu,
    suggestion.suggested_women_prayer_area,
  ].some(value => value !== null && value !== undefined);
};

// Helper to get URL params
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    view: (params.get('view') as ViewType) || 'mosques',
    tab: (params.get('tab') as TabType) || 'pending',
    query: params.get('q') || '',
  };
};

const isMissingRelationError = (err: any, relationName: string): boolean => {
  const message = String(err?.message || '').toLowerCase();
  return (
    err?.code === '42P01' ||
    err?.code === 'PGRST205' ||
    message.includes(`relation "${relationName.toLowerCase()}" does not exist`) ||
    message.includes('could not find the table')
  );
};

// Helper to update URL params
const updateUrlParams = (view: ViewType, tab: TabType, query: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set('view', view);
  url.searchParams.set('tab', tab);
  if (query.trim()) {
    url.searchParams.set('q', query.trim());
  } else {
    url.searchParams.delete('q');
  }
  window.history.replaceState({}, '', url.toString());
};

export function AdminDashboard({ onClose }: AdminDashboardProps) {
  const { t } = useLanguage();
  const { user, isAdmin, loading: adminLoading, signOut } = useAdmin();
  const { fetchPhotosByStatus, approvePhoto, rejectPhoto, deletePhoto } = usePhotos();

  // Initialize from URL params
  const initialParams = getUrlParams();
  const [activeView, setActiveView] = useState<ViewType>(initialParams.view);
  const [activeTab, setActiveTab] = useState<TabType>(initialParams.tab);
  const [searchQuery, setSearchQuery] = useState(initialParams.query);
  const [mosques, setMosques] = useState<DatabaseMosque[]>([]);
  const [editSuggestions, setEditSuggestions] = useState<EditSuggestion[]>([]);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMosque, setSelectedMosque] = useState<DatabaseMosque | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<EditSuggestion | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<PendingPhoto | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const itemsPerPage = 20;
  const debouncedSearchQuery = useDebounce(searchQuery, 250);
  const normalizedSearchQuery = debouncedSearchQuery.trim().toLowerCase();
  const hasActiveSearch = normalizedSearchQuery.length > 0;

  // Cache refs to persist across renders
  const mosqueCache = useRef<Record<string, CacheEntry<DatabaseMosque>>>({});
  const suggestionCache = useRef<Record<string, CacheEntry<EditSuggestion>>>({});
  const photoCache = useRef<Record<string, CacheEntry<PendingPhoto>>>({});

  const getCacheKey = useCallback((view: ViewType, tab: TabType, page: number, query: string = ''): string => {
    return `${view}-${tab}-${page}-${query}`;
  }, []);

  const mosqueSearchCacheKey = activeView === 'mosques' ? normalizedSearchQuery : '';

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    // Clear all cache on refresh
    mosqueCache.current = {};
    suggestionCache.current = {};
    photoCache.current = {};
    if (activeView === 'mosques') {
      fetchMosques(true);
    } else if (activeView === 'editSuggestions') {
      fetchEditSuggestions(true);
    } else {
      fetchPhotos(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, activeTab, currentPage]);

  // Invalidate cache for current view/tab
  const invalidateCurrentCache = useCallback(() => {
    const cacheKey = getCacheKey(
      activeView,
      activeTab,
      currentPage,
      activeView === 'mosques' ? normalizedSearchQuery : ''
    );
    if (activeView === 'mosques') {
      delete mosqueCache.current[cacheKey];
    } else if (activeView === 'editSuggestions') {
      delete suggestionCache.current[cacheKey];
    } else {
      delete photoCache.current[cacheKey];
    }
  }, [activeView, activeTab, currentPage, getCacheKey, normalizedSearchQuery]);

  const invalidateAllCaches = useCallback(() => {
    mosqueCache.current = {};
    suggestionCache.current = {};
    photoCache.current = {};
  }, []);

  useEffect(() => {
    updateUrlParams(activeView, activeTab, debouncedSearchQuery);
  }, [activeView, activeTab, debouncedSearchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (isAdmin) {
      if (activeView === 'mosques') {
        fetchMosques(false);
      } else if (activeView === 'editSuggestions') {
        fetchEditSuggestions(false);
      } else {
        fetchPhotos(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin, currentPage, activeView, mosqueSearchCacheKey]);

  const fetchMosques = async (forceRefresh: boolean = false) => {
    const cacheKey = getCacheKey('mosques', activeTab, currentPage, normalizedSearchQuery);
    const cached = mosqueCache.current[cacheKey];

    // Use cache if available and not forcing refresh
    if (!forceRefresh && cached) {
      setMosques(cached.data);
      setTotalCount(cached.count);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('mosques')
        .select('*', { count: 'exact' })
        .eq('status', activeTab);

      if (hasActiveSearch) {
        const searchPattern = `*${escapePostgrestLikeValue(normalizedSearchQuery)}*`;
        query = query.or([
          `name.ilike.${searchPattern}`,
          `address.ilike.${searchPattern}`,
          `description.ilike.${searchPattern}`,
          `created_by_name.ilike.${searchPattern}`,
          `rejection_reason.ilike.${searchPattern}`,
        ].join(','));
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Update cache
      mosqueCache.current[cacheKey] = {
        data: data || [],
        count: count || 0,
        timestamp: Date.now(),
      };

      setTotalCount(count || 0);
      setMosques(data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error fetching mosques:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchEditSuggestions = async (forceRefresh: boolean = false) => {
    const cacheKey = getCacheKey('editSuggestions', activeTab, currentPage);
    const cached = suggestionCache.current[cacheKey];

    // Use cache if available and not forcing refresh
    if (!forceRefresh && cached) {
      setEditSuggestions(cached.data);
      setSuggestionCount(cached.count);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error, count } = await supabase
        .from('mosque_edit_suggestions')
        .select(`
          *,
          mosques (
            name,
            type,
            is_public,
            description,
            men_wc,
            women_wc,
            men_wudu,
            women_wudu,
            women_prayer_area,
            latitude,
            longitude
          )
        `, { count: 'exact' })
        .eq('status', activeTab)
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Update cache
      suggestionCache.current[cacheKey] = {
        data: (data as EditSuggestion[]) || [],
        count: count || 0,
        timestamp: Date.now(),
      };

      setSuggestionCount(count || 0);
      setEditSuggestions((data as EditSuggestion[]) || []);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error fetching edit suggestions:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchPhotos = async (forceRefresh: boolean = false) => {
    const cacheKey = getCacheKey('photos', activeTab, currentPage);
    const cached = photoCache.current[cacheKey];

    if (!forceRefresh && cached) {
      setPhotos(cached.data);
      setPhotoCount(cached.count);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, count } = await fetchPhotosByStatus(activeTab, currentPage, itemsPerPage);
      const typedData = data as PendingPhoto[];

      photoCache.current[cacheKey] = {
        data: typedData,
        count,
        timestamp: Date.now(),
      };

      setPhotos(typedData);
      setPhotoCount(count);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error fetching photos:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleApproveSuggestion = async (suggestion: EditSuggestion) => {
    const parsedReason = parseReportReason(suggestion.reason);
    if (parsedReason.isReport && parsedReason.code === 'closed_or_moved') {
      if (!window.confirm(t('admin.confirmApproveClosedOrMovedReport'))) {
        return;
      }
    }

    const suggestionId = suggestion.id;
    setProcessingId(suggestionId);
    try {
      const { error } = await supabase.rpc('apply_edit_suggestion', {
        suggestion_id: suggestionId
      });

      if (error) throw error;

      invalidateCurrentCache();
      setEditSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      setSelectedSuggestion(null);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error approving suggestion:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectSuggestion = async (suggestionId: string) => {
    if (!rejectionReason.trim()) {
      setError(t('admin.provideRejectionReason'));
      return;
    }

    setProcessingId(suggestionId);
    try {
      const { error } = await supabase.rpc('reject_edit_suggestion', {
        suggestion_id: suggestionId,
        reject_reason: rejectionReason
      });

      if (error) throw error;

      invalidateCurrentCache();
      setEditSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      setSelectedSuggestion(null);
      setRejectionReason('');
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error rejecting suggestion:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteSuggestion = async (suggestionId: string) => {
    if (!window.confirm(t('admin.confirmDeleteSuggestion'))) {
      return;
    }

    setProcessingId(suggestionId);
    try {
      const { error } = await supabase
        .from('mosque_edit_suggestions')
        .delete()
        .eq('id', suggestionId);

      if (error) throw error;

      invalidateCurrentCache();
      setEditSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      setSelectedSuggestion(null);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error deleting suggestion:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprovePhoto = async (photoId: string) => {
    setProcessingId(photoId);
    try {
      await approvePhoto(photoId);
      invalidateCurrentCache();
      setPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
      setSelectedPhoto(null);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error approving photo:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectPhoto = async (photoId: string) => {
    if (!rejectionReason.trim()) {
      setError(t('admin.provideRejectionReason'));
      return;
    }

    setProcessingId(photoId);
    try {
      await rejectPhoto(photoId, rejectionReason);
      invalidateCurrentCache();
      setPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
      setSelectedPhoto(null);
      setRejectionReason('');
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error rejecting photo:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeletePhoto = async (photo: PendingPhoto) => {
    if (!window.confirm(t('admin.confirmDeletePhoto'))) {
      return;
    }

    setProcessingId(photo.id);
    try {
      await deletePhoto(photo.id, photo.storage_path);
      invalidateCurrentCache();
      setPhotos((prev) => prev.filter((item) => item.id !== photo.id));
      setSelectedPhoto(null);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error deleting photo:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprove = async (mosqueId: string) => {
    setProcessingId(mosqueId);
    try {
      const { error } = await supabase.rpc('approve_mosque', {
        mosque_id: mosqueId
      });

      if (error) throw error;

      invalidateCurrentCache();
      setMosques(prev => prev.filter(m => m.id !== mosqueId));
      setSelectedMosque(null);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error approving mosque:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (mosqueId: string) => {
    if (!rejectionReason.trim()) {
      setError(t('admin.provideRejectionReason'));
      return;
    }

    setProcessingId(mosqueId);
    try {
      const { error } = await supabase.rpc('reject_mosque', {
        mosque_id: mosqueId,
        reason: rejectionReason
      });

      if (error) throw error;

      invalidateCurrentCache();
      setMosques(prev => prev.filter(m => m.id !== mosqueId));
      setSelectedMosque(null);
      setRejectionReason('');
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error rejecting mosque:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (mosqueId: string) => {
    if (!window.confirm(t('admin.confirmDelete'))) {
      return;
    }

    setProcessingId(mosqueId);
    try {
      const { data: photoRows, error: photoRowsError } = await supabase
        .from('mosque_photos')
        .select('storage_path')
        .eq('mosque_id', mosqueId);

      if (photoRowsError && !isMissingRelationError(photoRowsError, 'mosque_photos')) {
        throw photoRowsError;
      }

      const photoPaths = (photoRows || [])
        .map((row) => row.storage_path)
        .filter((path): path is string => Boolean(path));

      const { error: suggestionDeleteError } = await supabase
        .from('mosque_edit_suggestions')
        .delete()
        .eq('mosque_id', mosqueId);

      if (suggestionDeleteError && !isMissingRelationError(suggestionDeleteError, 'mosque_edit_suggestions')) {
        throw suggestionDeleteError;
      }

      const { error: photoDeleteError } = await supabase
        .from('mosque_photos')
        .delete()
        .eq('mosque_id', mosqueId);

      if (photoDeleteError && !isMissingRelationError(photoDeleteError, 'mosque_photos')) {
        throw photoDeleteError;
      }

      const { error } = await supabase
        .from('mosques')
        .delete()
        .eq('id', mosqueId);

      if (error) throw error;

      if (photoPaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from(PHOTO_BUCKET)
          .remove(photoPaths);
        if (storageError) {
          // Keep deletion successful even if a storage object is already missing.
          console.error('Failed to remove one or more photo files:', storageError);
        }
      }

      invalidateAllCaches();
      setMosques(prev => prev.filter(m => m.id !== mosqueId));
      setTotalCount(prev => Math.max(0, prev - 1));
      setSelectedMosque(null);
    } catch (err: any) {
      setError(getErrorMessage(err, t));
      console.error('Error deleting mosque:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'mosque': return t('mosqueTypes.mosque');
      case 'prayer_room': return t('mosqueTypes.prayerRoom');
      case 'hired_hall': return t('mosqueTypes.hiredHall');
      default: return type;
    }
  };

  const getFacilityLabel = (value: boolean | null) => {
    if (value === true) return <Check size={14} />;
    if (value === false) return <X size={14} />;
    return <HelpCircle size={14} />;
  };

  const getReportTypeLabel = (code: string | null) => {
    switch (code) {
      case 'closed_or_moved':
        return t('admin.reportClosedOrMoved');
      case 'temporarily_closed':
        return t('admin.reportTemporarilyClosed');
      case 'wrong_info':
        return t('admin.reportWrongInfo');
      default:
        return t('admin.issueReport');
    }
  };

  const filteredMosques = useMemo(() => {
    if (!hasActiveSearch) {
      return mosques;
    }

    return mosques.filter((mosque) =>
      [
        mosque.name,
        mosque.address,
        mosque.description,
        mosque.created_by_name,
        mosque.rejection_reason,
      ].some((value) => value?.toLowerCase().includes(normalizedSearchQuery))
    );
  }, [hasActiveSearch, mosques, normalizedSearchQuery]);

  const filteredSuggestions = useMemo(() => {
    if (!hasActiveSearch) {
      return editSuggestions;
    }

    return editSuggestions.filter((suggestion) =>
      [
        suggestion.mosques?.name,
        suggestion.suggested_name,
        suggestion.reason,
        suggestion.submitted_by_name,
        suggestion.rejection_reason,
      ].some((value) => value?.toLowerCase().includes(normalizedSearchQuery))
    );
  }, [hasActiveSearch, editSuggestions, normalizedSearchQuery]);

  const filteredPhotos = useMemo(() => {
    if (!hasActiveSearch) {
      return photos;
    }

    return photos.filter((photo) =>
      [
        photo.mosques?.name,
        photo.file_name,
        photo.caption,
        photo.rejection_reason,
      ].some((value) => value?.toLowerCase().includes(normalizedSearchQuery))
    );
  }, [hasActiveSearch, photos, normalizedSearchQuery]);

  if (adminLoading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h2>{t('admin.dashboard')}</h2>
          <button onClick={onClose} className="close-button"><X size={18} /></button>
        </div>
        <div className="admin-content">
          <p className="loading-message">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h2>{t('admin.dashboard')}</h2>
          <button onClick={onClose} className="close-button"><X size={18} /></button>
        </div>
        <div className="admin-content">
          <p className="error-message">{t('admin.noPrivileges')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h2>{t('admin.dashboard')}</h2>
        <div className="header-actions">
          <button
            onClick={handleRefresh}
            className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
            disabled={isRefreshing}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <span className="user-email">{user?.email}</span>
          <button onClick={signOut} className="signout-button">{t('admin.signOut')}</button>
          <button onClick={onClose} className="close-button"><X size={18} /></button>
        </div>
      </div>

      <div className="admin-view-toggle">
        <button
          className={`view-toggle-btn ${activeView === 'mosques' ? 'active' : ''}`}
          onClick={() => {
            setActiveView('mosques');
            setCurrentPage(1);
          }}
        >
          {t('admin.newMosques')}
        </button>
        <button
          className={`view-toggle-btn ${activeView === 'editSuggestions' ? 'active' : ''}`}
          onClick={() => {
            setActiveView('editSuggestions');
            setCurrentPage(1);
          }}
        >
          {t('admin.editSuggestions')}
        </button>
        <button
          className={`view-toggle-btn ${activeView === 'photos' ? 'active' : ''}`}
          onClick={() => {
            setActiveView('photos');
            setCurrentPage(1);
          }}
        >
          {t('admin.photos')}
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('pending');
            setCurrentPage(1);
          }}
        >
          {t('admin.pending')} ({activeTab === 'pending' ? (activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) : ''})
        </button>
        <button
          className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('approved');
            setCurrentPage(1);
          }}
        >
          {t('admin.approved')} {activeTab === 'approved' && (activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) > 0 && `(${activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount})`}
        </button>
        <button
          className={`tab ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('rejected');
            setCurrentPage(1);
          }}
        >
          {t('admin.rejected')} {activeTab === 'rejected' && (activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) > 0 && `(${activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount})`}
        </button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="admin-content">
        <div className="admin-search-row">
          <div className="admin-search-input-wrapper">
            <Search size={16} className="admin-search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.searchPlaceholder')}
              className="admin-search-input"
            />
            {searchQuery.trim() && (
              <button
                type="button"
                className="admin-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label={t('common.clear')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {loading && !isRefreshing ? (
          <div className="loading">{t('common.loading')}...</div>
        ) : activeView === 'editSuggestions' ? (
          editSuggestions.length === 0 ? (
            <div className="empty-state">
              {activeTab === 'pending' ? t('admin.noPendingSuggestions') :
               activeTab === 'approved' ? t('admin.noApprovedSuggestions') :
               t('admin.noRejectedSuggestions')}
            </div>
          ) : filteredSuggestions.length === 0 ? (
            <div className="empty-state">
              {t('admin.noSearchResults').replace('{query}', debouncedSearchQuery.trim())}
            </div>
          ) : (
            <div className="mosque-grid">
              {filteredSuggestions.map(suggestion => {
                const parsedReason = parseReportReason(suggestion.reason);
                const isIssueReport = parsedReason.isReport;
                const hasFieldChanges = hasSuggestedFieldChanges(suggestion);

                return (
                <div key={suggestion.id} className="mosque-card suggestion-card">
                  <div className="mosque-card-header">
                    <h3>{suggestion.mosques?.name || t('admin.unknownMosque')}</h3>
                    <span className={`mosque-type ${isIssueReport ? 'issue-badge' : 'suggestion-badge'}`}>
                      {isIssueReport ? t('admin.issueReport') : t('admin.editSuggestion')}
                    </span>
                  </div>

                  <div className="mosque-card-body">
                    {(parsedReason.details || isIssueReport) && (
                      <div className="suggestion-reason">
                        <strong>{isIssueReport ? t('admin.reportType') : t('admin.reason')}:</strong>{' '}
                        <span className="multiline-text">
                          {isIssueReport ? getReportTypeLabel(parsedReason.code) : parsedReason.details}
                        </span>
                        {isIssueReport && parsedReason.details && (
                          <p className="suggestion-note multiline-text">{parsedReason.details}</p>
                        )}
                      </div>
                    )}

                    <div className="suggestion-changes">
                      <strong>{t('admin.suggestedChanges')}:</strong>
                      {hasFieldChanges ? (
                        <ul className="changes-list">
                          {suggestion.suggested_name && suggestion.suggested_name !== suggestion.mosques?.name && (
                            <li>{t('addMosque.name')}: <span className="old-value">{suggestion.mosques?.name}</span> → <span className="new-value">{suggestion.suggested_name}</span></li>
                          )}
                          {suggestion.suggested_type && suggestion.suggested_type !== suggestion.mosques?.type && (
                            <li>{t('addMosque.type')}: <span className="old-value">{getTypeLabel(suggestion.mosques?.type || '')}</span> → <span className="new-value">{getTypeLabel(suggestion.suggested_type)}</span></li>
                          )}
                          {suggestion.suggested_is_public !== null && suggestion.suggested_is_public !== suggestion.mosques?.is_public && (
                            <li>{t('addMosque.access')}: <span className="old-value">{suggestion.mosques?.is_public ? t('mosqueDetails.public') : t('mosqueDetails.private')}</span> → <span className="new-value">{suggestion.suggested_is_public ? t('mosqueDetails.public') : t('mosqueDetails.private')}</span></li>
                          )}
                          {suggestion.suggested_latitude !== null && suggestion.suggested_latitude !== suggestion.mosques?.latitude && (
                            <li>{t('suggestEdit.latitudeLabel')}: <span className="old-value">{suggestion.mosques?.latitude.toFixed(6)}</span> → <span className="new-value">{suggestion.suggested_latitude.toFixed(6)}</span></li>
                          )}
                          {suggestion.suggested_longitude !== null && suggestion.suggested_longitude !== suggestion.mosques?.longitude && (
                            <li>{t('suggestEdit.longitudeLabel')}: <span className="old-value">{suggestion.mosques?.longitude.toFixed(6)}</span> → <span className="new-value">{suggestion.suggested_longitude.toFixed(6)}</span></li>
                          )}
                          {suggestion.suggested_description && suggestion.suggested_description !== suggestion.mosques?.description && (
                            <li>{t('mosqueDetails.description')}: <span className="new-value multiline-text">{suggestion.suggested_description}</span></li>
                          )}
                          {suggestion.suggested_men_wc !== null && suggestion.suggested_men_wc !== suggestion.mosques?.men_wc && (
                            <li>{t('admin.menWc')}: <span className="old-value">{getFacilityLabel(suggestion.mosques?.men_wc ?? null)}</span> → <span className="new-value">{getFacilityLabel(suggestion.suggested_men_wc)}</span></li>
                          )}
                          {suggestion.suggested_women_wc !== null && suggestion.suggested_women_wc !== suggestion.mosques?.women_wc && (
                            <li>{t('admin.womenWc')}: <span className="old-value">{getFacilityLabel(suggestion.mosques?.women_wc ?? null)}</span> → <span className="new-value">{getFacilityLabel(suggestion.suggested_women_wc)}</span></li>
                          )}
                          {suggestion.suggested_men_wudu !== null && suggestion.suggested_men_wudu !== suggestion.mosques?.men_wudu && (
                            <li>{t('admin.menWudu')}: <span className="old-value">{getFacilityLabel(suggestion.mosques?.men_wudu ?? null)}</span> → <span className="new-value">{getFacilityLabel(suggestion.suggested_men_wudu)}</span></li>
                          )}
                          {suggestion.suggested_women_wudu !== null && suggestion.suggested_women_wudu !== suggestion.mosques?.women_wudu && (
                            <li>{t('admin.womenWudu')}: <span className="old-value">{getFacilityLabel(suggestion.mosques?.women_wudu ?? null)}</span> → <span className="new-value">{getFacilityLabel(suggestion.suggested_women_wudu)}</span></li>
                          )}
                          {suggestion.suggested_women_prayer_area !== null && suggestion.suggested_women_prayer_area !== suggestion.mosques?.women_prayer_area && (
                            <li>{t('admin.womenArea')}: <span className="old-value">{getFacilityLabel(suggestion.mosques?.women_prayer_area ?? null)}</span> → <span className="new-value">{getFacilityLabel(suggestion.suggested_women_prayer_area)}</span></li>
                          )}
                        </ul>
                      ) : (
                        <p className="suggestion-note">{t('admin.noFieldChanges')}</p>
                      )}
                    </div>

                    <div className="mosque-meta">
                      <p>{t('admin.submitted')}: {new Date(suggestion.created_at).toLocaleDateString()}</p>
                      {suggestion.submitted_by_name && (
                        <p>{t('admin.addedBy')}: {suggestion.submitted_by_name}</p>
                      )}
                      {suggestion.reviewed_at && (
                        <p>{t('admin.reviewed')}: {new Date(suggestion.reviewed_at).toLocaleDateString()}</p>
                      )}
                    </div>

                    {activeTab === 'rejected' && suggestion.rejection_reason && (
                      <div className="rejection-reason">
                        <strong>{t('admin.rejectionReason')}:</strong>
                        <p className="multiline-text">{suggestion.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  <div className="mosque-card-actions">
                    {activeTab === 'pending' && (
                      <>
                        <button
                          className="approve-button"
                          onClick={() => handleApproveSuggestion(suggestion)}
                          disabled={processingId === suggestion.id}
                        >
                          {processingId === suggestion.id ? t('admin.processing') : t('admin.approve')}
                        </button>
                        <button
                          className="reject-button"
                          onClick={() => setSelectedSuggestion(suggestion)}
                          disabled={processingId === suggestion.id}
                        >
                          {t('admin.reject')}
                        </button>
                      </>
                    )}
                    {suggestion.mosques && (
                      <button
                        className="view-button"
                        onClick={() => window.open(`${window.location.origin}?lat=${suggestion.mosques!.latitude}&lng=${suggestion.mosques!.longitude}&zoom=18`, '_blank')}
                      >
                        {t('admin.viewOnMap')}
                      </button>
                    )}
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteSuggestion(suggestion.id)}
                      disabled={processingId === suggestion.id}
                    >
                      {t('admin.delete')}
                    </button>
                  </div>
                </div>
              )})}
            </div>
          )
        ) : activeView === 'photos' ? (
          photos.length === 0 ? (
            <div className="empty-state">
              {activeTab === 'pending' ? t('admin.noPendingPhotos') :
               activeTab === 'approved' ? t('admin.noApprovedPhotos') :
               t('admin.noRejectedPhotos')}
            </div>
          ) : filteredPhotos.length === 0 ? (
            <div className="empty-state">
              {t('admin.noSearchResults').replace('{query}', debouncedSearchQuery.trim())}
            </div>
          ) : (
            <div className="mosque-grid">
              {filteredPhotos.map(photo => (
                <div key={photo.id} className="mosque-card">
                  <div className="mosque-card-header">
                    <h3>{photo.mosques?.name || t('admin.unknownMosque')}</h3>
                    <span className="mosque-type photo-badge">
                      <Images size={12} /> {t('admin.photo')}
                    </span>
                  </div>

                  <div className="mosque-card-body">
                    <img src={photo.photo_url} alt={photo.file_name} className="admin-photo-preview" />
                    {photo.caption && (
                      <p className="mosque-description">{photo.caption}</p>
                    )}
                    <div className="mosque-meta">
                      <p>{t('admin.submitted')}: {new Date(photo.created_at).toLocaleDateString()}</p>
                    </div>
                    {activeTab === 'rejected' && photo.rejection_reason && (
                      <div className="rejection-reason">
                        <strong>{t('admin.rejectionReason')}:</strong>
                        <p className="multiline-text">{photo.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  <div className="mosque-card-actions">
                    {activeTab === 'pending' && (
                      <>
                        <button
                          className="approve-button"
                          onClick={() => handleApprovePhoto(photo.id)}
                          disabled={processingId === photo.id}
                        >
                          {processingId === photo.id ? t('admin.processing') : t('admin.approve')}
                        </button>
                        <button
                          className="reject-button"
                          onClick={() => setSelectedPhoto(photo)}
                          disabled={processingId === photo.id}
                        >
                          {t('admin.reject')}
                        </button>
                      </>
                    )}
                    <button
                      className="delete-button"
                      onClick={() => handleDeletePhoto(photo)}
                      disabled={processingId === photo.id}
                    >
                      {t('admin.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : mosques.length === 0 ? (
          <div className="empty-state">
            {activeTab === 'pending' ? t('admin.noPendingMosques') :
             activeTab === 'approved' ? t('admin.noApprovedMosques') :
             t('admin.noRejectedMosques')}
          </div>
        ) : filteredMosques.length === 0 ? (
          <div className="empty-state">
            {t('admin.noSearchResults').replace('{query}', debouncedSearchQuery.trim())}
          </div>
        ) : (
          <div className="mosque-grid">
            {filteredMosques.map(mosque => (
              <div key={mosque.id} className="mosque-card">
                <div className="mosque-card-header">
                  <h3>{mosque.name}</h3>
                  <span className="mosque-type">{getTypeLabel(mosque.type)}</span>
                </div>
                
                <div className="mosque-card-body">
                  <p className="mosque-address">{mosque.address || t('admin.noAddress')}</p>

                  {mosque.description && (
                    <p className="mosque-description">{mosque.description}</p>
                  )}

                  <div className="facilities-grid">
                    <div className="facility">
                      <span>{t('admin.menWc')}:</span>
                      <span className={`facility-value ${mosque.men_wc ? 'yes' : mosque.men_wc === false ? 'no' : 'unknown'}`}>
                        {getFacilityLabel(mosque.men_wc)}
                      </span>
                    </div>
                    <div className="facility">
                      <span>{t('admin.womenWc')}:</span>
                      <span className={`facility-value ${mosque.women_wc ? 'yes' : mosque.women_wc === false ? 'no' : 'unknown'}`}>
                        {getFacilityLabel(mosque.women_wc)}
                      </span>
                    </div>
                    <div className="facility">
                      <span>{t('admin.menWudu')}:</span>
                      <span className={`facility-value ${mosque.men_wudu ? 'yes' : mosque.men_wudu === false ? 'no' : 'unknown'}`}>
                        {getFacilityLabel(mosque.men_wudu)}
                      </span>
                    </div>
                    <div className="facility">
                      <span>{t('admin.womenWudu')}:</span>
                      <span className={`facility-value ${mosque.women_wudu ? 'yes' : mosque.women_wudu === false ? 'no' : 'unknown'}`}>
                        {getFacilityLabel(mosque.women_wudu)}
                      </span>
                    </div>
                    <div className="facility">
                      <span>{t('admin.womenArea')}:</span>
                      <span className={`facility-value ${mosque.women_prayer_area ? 'yes' : mosque.women_prayer_area === false ? 'no' : 'unknown'}`}>
                        {getFacilityLabel(mosque.women_prayer_area)}
                      </span>
                    </div>
                  </div>

                  <div className="mosque-meta">
                    {mosque.created_by_name && (
                      <p>{t('admin.addedBy')}: {mosque.created_by_name}</p>
                    )}
                    <p>{t('admin.submitted')}: {new Date(mosque.created_at).toLocaleDateString()}</p>
                    {mosque.reviewed_at && (
                      <p>{t('admin.reviewed')}: {new Date(mosque.reviewed_at).toLocaleDateString()}</p>
                    )}
                  </div>

                  {activeTab === 'rejected' && mosque.rejection_reason && (
                    <div className="rejection-reason">
                      <strong>{t('admin.rejectionReason')}:</strong>
                      <p className="multiline-text">{mosque.rejection_reason}</p>
                    </div>
                  )}
                </div>

                <div className="mosque-card-actions">
                  {activeTab === 'pending' && (
                    <>
                      <button
                        className="approve-button"
                        onClick={() => handleApprove(mosque.id)}
                        disabled={processingId === mosque.id}
                      >
                        {processingId === mosque.id ? t('admin.processing') : t('admin.approve')}
                      </button>
                      <button
                        className="reject-button"
                        onClick={() => setSelectedMosque(mosque)}
                        disabled={processingId === mosque.id}
                      >
                        {t('admin.reject')}
                      </button>
                    </>
                  )}
                  <button
                    className="view-button"
                    onClick={() => window.open(`${window.location.origin}?lat=${mosque.latitude}&lng=${mosque.longitude}&zoom=18`, '_blank')}
                  >
                    {t('admin.viewOnMap')}
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(mosque.id)}
                    disabled={processingId === mosque.id}
                  >
                    {t('admin.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!loading && (activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) > itemsPerPage && (
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              {t('admin.previous')}
            </button>
            <span className="pagination-info">
              {t('admin.pageOf')
                .replace('{current}', String(currentPage))
                .replace('{total}', String(Math.ceil((activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) / itemsPerPage)))
                .replace('{count}', String(activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount))}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil((activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) / itemsPerPage), prev + 1))}
              disabled={currentPage >= Math.ceil((activeView === 'mosques' ? totalCount : activeView === 'editSuggestions' ? suggestionCount : photoCount) / itemsPerPage)}
              className="pagination-btn"
            >
              {t('admin.next')}
            </button>
          </div>
        )}
      </div>

      {selectedMosque && (
        <div className="modal-overlay" onClick={() => setSelectedMosque(null)}>
          <div className="rejection-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('admin.rejectMosque')}: {selectedMosque.name}</h3>
            <textarea
              placeholder={t('admin.rejectionPlaceholder')}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setSelectedMosque(null);
                  setRejectionReason('');
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="confirm-reject-button"
                onClick={() => handleReject(selectedMosque.id)}
                disabled={processingId === selectedMosque.id}
              >
                {processingId === selectedMosque.id ? t('admin.processing') : t('admin.confirmRejection')}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSuggestion && (
        <div className="modal-overlay" onClick={() => setSelectedSuggestion(null)}>
          <div className="rejection-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('admin.rejectSuggestion')}: {selectedSuggestion.mosques?.name}</h3>
            <textarea
              placeholder={t('admin.rejectionPlaceholder')}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setSelectedSuggestion(null);
                  setRejectionReason('');
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="confirm-reject-button"
                onClick={() => handleRejectSuggestion(selectedSuggestion.id)}
                disabled={processingId === selectedSuggestion.id}
              >
                {processingId === selectedSuggestion.id ? t('admin.processing') : t('admin.confirmRejection')}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedPhoto && (
        <div className="modal-overlay" onClick={() => setSelectedPhoto(null)}>
          <div className="rejection-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('admin.rejectPhoto')}</h3>
            <textarea
              placeholder={t('admin.rejectionPlaceholder')}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setSelectedPhoto(null);
                  setRejectionReason('');
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="confirm-reject-button"
                onClick={() => handleRejectPhoto(selectedPhoto.id)}
                disabled={processingId === selectedPhoto.id}
              >
                {processingId === selectedPhoto.id ? t('admin.processing') : t('admin.confirmRejection')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
